import type { TFunction } from 'i18next';

export function joinStringsWithCommasAnd(words: string[], t: TFunction) {
  const start = words.slice(0, -1).join(', ');
  const and = ` ${t('conjunctions.and')} `;
  return start.length ? [start, words[words.length - 1]].join(and) : words.slice(-2).join(and);
}

// This makes every word start with an uppercase letter, regardless of whether it's an unimportant word like 'a' or 'in'.
export function simpleTitleCase(str: string) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Masks an email address for display, protecting PII while keeping it recognizable.
 * Examples:
 *   - "skinner@ava.me" → "sk****@a**.me"
 *   - "john.doe@gmail.com" → "jo****@g****.com"
 *   - "a@b.co" → "a@b.co" (too short to mask meaningfully)
 */
export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) {
    return email || '';
  }

  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return email;
  }

  // Mask local part: show first 2 chars (or 1 if short), replace rest with ****
  let maskedLocal: string;
  if (localPart.length <= 2) {
    maskedLocal = localPart; // Too short to mask
  } else {
    const visibleChars = Math.min(2, localPart.length);
    maskedLocal = localPart.slice(0, visibleChars) + '****';
  }

  // Mask domain: show first char + mask + TLD
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return `${maskedLocal}@${domain}`;
  }

  const tld = domainParts[domainParts.length - 1]; // e.g., "com", "me"
  const domainName = domainParts.slice(0, -1).join('.'); // e.g., "gmail", "ava"

  let maskedDomain: string;
  if (domainName.length <= 1) {
    maskedDomain = domainName; // Too short to mask
  } else {
    const maskLength = Math.min(5, domainName.length - 1);
    maskedDomain = domainName.charAt(0) + '*'.repeat(maskLength);
  }

  return `${maskedLocal}@${maskedDomain}.${tld}`;
}
